import dayjs from 'dayjs';
import React from 'react';

import { formatAge } from 'ha/utils/formatAge';
import { useIntl } from 'ha/i18n';
import { UserEntity } from 'ha/types/UserEntity';
import { formatGender } from 'ha/utils/formatGender';
import * as Section from 'ha/ui.legacy/Section';
import { languages } from 'ha/constants/SpokenLanguages';
import { OCCUPATION, OCCUPATION_TRANSLATIONS } from 'ha/constants/Occupation';
import { Gender } from 'ha/types/Gender';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  root: {
    overflow: 'hidden',
  },
}));

interface Props {
  user: UserEntity;
}

const getOccupation = (user: {
  occupationDetail: string;
  occupation: string;
}) => {
  let occupation: string = user.occupationDetail;

  if (user.occupation !== OCCUPATION.unknown) {
    occupation =
      user.occupation === OCCUPATION.other && !!user.occupationDetail
        ? user.occupationDetail
        : OCCUPATION_TRANSLATIONS[user.occupation];
  }

  return occupation;
};

const UserCardInfoInternal: React.FC<Props> = ({ user }) => {
  const occupation = getOccupation(user);
  const hasInfo =
    dayjs(user.birthDate).isValid() ||
    user.study ||
    user.occupation ||
    user.gender ||
    (user.spokenLanguages && user.spokenLanguages.length) ||
    dayjs(user.registeredSince).isValid();

  const { classes } = useStyles();
  const { T, formatDate } = useIntl();

  if (!hasInfo) {
    return null;
  }

  return (
    <section className={classes.root}>
      {dayjs(user.birthDate).isValid() && (
        <Section.Text data-test-locator="User Card Age">
          <dt>{T('Age')}</dt>
          <dd>{T('%d years old', formatAge(user.birthDate))}</dd>
        </Section.Text>
      )}
      {occupation && (
        <Section.Text data-test-locator="User Card Occupation">
          <dt>{T('Occupation')}</dt>
          <dd>{T(occupation)}</dd>
        </Section.Text>
      )}
      {user.occupation === OCCUPATION.workingProfessional &&
        !!user.occupationDetail && (
          <Section.Text data-test-locator="User Card Working Professional">
            <dt>{T('Job title')}</dt>
            <dd>{user.occupationDetail}</dd>
          </Section.Text>
        )}
      {user.occupation === OCCUPATION.student && !!user.occupationDetail && (
        <Section.Text data-test-locator="User Card School University">
          <dt>{T('School/University')}</dt>
          <dd>{user.occupationDetail}</dd>
        </Section.Text>
      )}
      {user.spokenLanguages.length > 0 && (
        <Section.Text data-test-locator="User Card Languages">
          <dt>{T('Languages')}</dt>
          <dd>
            {user.spokenLanguages
              .filter(Boolean)
              .map(langCode => languages[langCode])
              .map(T)
              .join(', ')}
          </dd>
        </Section.Text>
      )}
      {dayjs(user.registeredSince).isValid() && (
        <Section.Text data-test-locator="User Card Member Since">
          <dt>{T('Member since')}</dt>
          <dd>{formatDate('MMMM YYYY', user.registeredSince)}</dd>
        </Section.Text>
      )}
      {user.gender === Gender.male || user.gender === Gender.female ? (
        <Section.Text data-test-locator="User Card Gender">
          <dt>{T('Gender')}</dt>
          <dd>{formatGender(T, user.gender)}</dd>
        </Section.Text>
      ) : null}
    </section>
  );
};

const UserCardInfo = UserCardInfoInternal;
export { UserCardInfo };
